import { StructuredJsonFieldDef } from '../../shared/design-system/structured-json-form.model';
import {
  emptyStructuredJsonItem,
  parseStructuredJsonArray,
  parseStructuredJsonPayload,
  serializeStructuredJsonArray,
  serializeStructuredJsonPayload
} from '../../shared/design-system/structured-json-form.utils';

export const STOREFRONT_PUBLICATION_BADGE_FIELDS: StructuredJsonFieldDef[] = [
  {
    key: 'code',
    type: 'select',
    labelKey: 'catalog.storefront.publishing.badges.code',
    options: [
      { value: 'new', labelKey: 'catalog.storefront.publishing.badges.code.new' },
      { value: 'sale', labelKey: 'catalog.storefront.publishing.badges.code.sale' },
      { value: 'featured', labelKey: 'catalog.storefront.publishing.badges.code.featured' },
      { value: 'credit', labelKey: 'catalog.storefront.publishing.badges.code.credit' },
      { value: 'inStock', labelKey: 'catalog.storefront.publishing.badges.code.inStock' }
    ]
  },
  { key: 'fa-IR', type: 'string', labelKey: 'catalog.storefront.fields.labelFa' },
  { key: 'en-US', type: 'string', labelKey: 'catalog.storefront.fields.labelEn' },
  {
    key: 'variant',
    type: 'select',
    labelKey: 'catalog.storefront.publishing.badges.variant',
    options: [
      { value: 'primary', labelKey: 'catalog.storefront.publishing.badges.variant.primary' },
      { value: 'success', labelKey: 'catalog.storefront.publishing.badges.variant.success' },
      { value: 'light', labelKey: 'catalog.storefront.publishing.badges.variant.light' },
      { value: 'warning', labelKey: 'catalog.storefront.publishing.badges.variant.warning' }
    ]
  }
];

export const STOREFRONT_PUBLICATION_SEO_FIELDS: StructuredJsonFieldDef[] = [
  { key: 'titleFa', type: 'string', labelKey: 'catalog.storefront.publishing.seo.titleFa' },
  { key: 'titleEn', type: 'string', labelKey: 'catalog.storefront.publishing.seo.titleEn' },
  { key: 'descriptionFa', type: 'textarea', labelKey: 'catalog.storefront.publishing.seo.descriptionFa' },
  { key: 'descriptionEn', type: 'textarea', labelKey: 'catalog.storefront.publishing.seo.descriptionEn' },
  { key: 'keywords', type: 'string', labelKey: 'catalog.storefront.publishing.seo.keywords' }
];

export function parsePublicationBadgesJson(raw?: string | null): string[] {
  const items = parseStructuredJsonArray(raw).map(item => normalizeLegacyBadgeItem(item));
  if (items.length === 0) {
    return [];
  }
  return items.map(item => serializeStructuredJsonPayload(item));
}

export function serializePublicationBadgesJson(badgePayloads: string[]): string {
  const items = badgePayloads
    .map(payload => normalizeLegacyBadgeItem(parseStructuredJsonPayload(payload)))
    .filter(item => hasBadgeContent(item));
  return serializeStructuredJsonArray(STOREFRONT_PUBLICATION_BADGE_FIELDS, items);
}

export function createEmptyPublicationBadgePayload(): string {
  return serializeStructuredJsonPayload(emptyStructuredJsonItem(STOREFRONT_PUBLICATION_BADGE_FIELDS));
}

function normalizeLegacyBadgeItem(item: Record<string, unknown>): Record<string, unknown> {
  const normalized = { ...item };
  if (!normalized['fa-IR'] && typeof normalized['labelFa'] === 'string') {
    normalized['fa-IR'] = normalized['labelFa'];
  }
  if (!normalized['en-US']) {
    if (typeof normalized['labelEn'] === 'string') {
      normalized['en-US'] = normalized['labelEn'];
    } else if (typeof normalized['label'] === 'string') {
      normalized['en-US'] = normalized['label'];
    }
  }
  if (!normalized['code'] && typeof normalized['type'] === 'string') {
    normalized['code'] = normalized['type'];
  }
  return normalized;
}

function hasBadgeContent(item: Record<string, unknown>): boolean {
  return Object.entries(item).some(([, value]) => {
    if (value === null || value === undefined) return false;
    return String(value).trim() !== '';
  });
}

export function normalizePublicationSeoJson(raw?: string | null): string {
  const parsed = parseStructuredJsonPayload(raw ?? '');
  const normalized: Record<string, unknown> = { ...parsed };
  if (!normalized['titleFa'] && typeof normalized['title'] === 'string') {
    normalized['titleFa'] = normalized['title'];
  }
  if (!normalized['titleEn'] && typeof normalized['title'] === 'string') {
    normalized['titleEn'] = normalized['title'];
  }
  if (!normalized['descriptionFa'] && typeof normalized['description'] === 'string') {
    normalized['descriptionFa'] = normalized['description'];
  }
  if (!normalized['descriptionEn'] && typeof normalized['description'] === 'string') {
    normalized['descriptionEn'] = normalized['description'];
  }
  return serializeStructuredJsonPayload(normalized);
}
